// Comments on a seed, SoundCloud style: written while it plays, a comment is
// pinned to that moment — its avatar sits on the waveform there, and its
// timestamp jumps back to it.
//
// "That moment" is the one you POINTED at when you clicked the waveform, not
// the one playback has run on to while the sentence was being typed: a click
// at 1:20 and thirty seconds of typing still means 1:20.

import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Trash2 } from "lucide-react";
import type { ApiComment, CommentsResponse } from "@shared/api-types.js";
import { Button } from "@web-shared/components/ui/button";
import { json, webApi } from "@web-shared/lib/web-api";
import { mediaTime, relativeTime } from "@web-shared/lib/format";
import { profilePath } from "@/lib/ctas";
import { useAuthGate } from "../auth";
import { useSession } from "../session";
import { usePlayer, useSeekMark, useTrackTime, type PlayerTrack } from "../player";
import { Avatar } from "./Avatar";
import type { WaveformMarker } from "./Waveform";

const MAX = 500;
/** Playback this far from the pinned moment is worth offering as the pin instead. */
const MOVED_ON_S = 2;

export function useSeedComments(seedId: string) {
  return useQuery({
    queryKey: ["web", "comments", seedId],
    staleTime: 60_000,
    queryFn: () => webApi<CommentsResponse>(`/seeds/${encodeURIComponent(seedId)}/comments`),
  });
}

/** Timed comments as marks on a waveform — only once the track's length is known. */
export function commentMarkers(comments: ApiComment[], durationMs: number | null | undefined): WaveformMarker[] {
  if (!durationMs) return [];
  return comments
    .filter((c) => c.atMs !== null && c.atMs <= durationMs)
    .map((c) => ({
      key: c.id,
      at: c.atMs! / durationMs,
      title: `${c.author.username} at ${mediaTime(c.atMs! / 1000)}: ${c.body}`,
      node: <Avatar name={c.author.username} url={c.author.avatarUrl} className="size-5 text-[8px] ring-2 ring-card" />,
    }));
}

export function Comments({ seed, master }: { seed: { id: string; title: string }; master: PlayerTrack | null }) {
  const comments = useSeedComments(seed.id);
  const list = comments.data?.comments ?? [];
  return (
    <section id="comments" className="rounded-xl border border-border bg-card">
      <h2 className="border-b border-border px-4 py-2.5 text-sm font-medium">
        Comments <span className="text-muted-foreground">· {list.length}</span>
      </h2>
      <Composer seed={seed} />
      {comments.isPending ? (
        <p className="px-4 py-3 text-xs text-muted-foreground">Loading…</p>
      ) : list.length === 0 ? (
        <p className="px-4 py-3 text-xs text-muted-foreground">No comments yet. Say what you hear — click the waveform where you mean and it pins there.</p>
      ) : (
        <ul className="divide-y divide-border">
          {[...list].reverse().map((c) => (
            <CommentRow key={c.id} comment={c} master={master} />
          ))}
        </ul>
      )}
    </section>
  );
}

function Composer({ seed }: { seed: { id: string; title: string } }) {
  const gate = useAuthGate();
  const session = useSession();
  const player = usePlayer();
  const queryClient = useQueryClient();
  const playingAt = useTrackTime(seed.id);
  // The spot a click on the waveform (or the bar, or another comment's "at")
  // chose. It outranks playback: pointing at a moment is saying which one.
  const clickedAt = useSeekMark(seed.id);
  const [text, setText] = useState("");
  const [pin, setPin] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Nothing was clicked: pinned to where playback was when you started
  // writing, not where it has drifted to by the time you press post.
  const [pinnedAt, setPinnedAt] = useState<number | null>(null);
  const moment = clickedAt ?? pinnedAt ?? playingAt;
  const at = pin ? moment : null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    gate.requireSignIn("comment", "Sign in to comment", () => {
      setBusy(true);
      setError(null);
      webApi<ApiComment>(`/seeds/${encodeURIComponent(seed.id)}/comments`, json("POST", { body, atMs: at === null ? null : Math.round(at * 1000) }))
        .then(() => {
          setText("");
          setPinnedAt(null);
          // The pin is spent: the next comment pins itself.
          player.clearMark();
          void queryClient.invalidateQueries({ queryKey: ["web", "comments", seed.id] });
          void queryClient.invalidateQueries({ queryKey: ["web", "seed", seed.id] });
        })
        .catch((err: unknown) => setError(err instanceof Error ? err.message : "Could not post."))
        .finally(() => setBusy(false));
    });
  };

  return (
    <form onSubmit={submit} className="space-y-2 border-b border-border px-4 py-3">
      <textarea
        value={text}
        onChange={(e) => {
          if (!text && e.target.value && playingAt !== null) setPinnedAt(playingAt);
          setText(e.target.value);
        }}
        maxLength={MAX}
        rows={2}
        placeholder={session ? "Write a comment" : "Sign in to comment"}
        aria-label="Write a comment"
        className="w-full resize-none rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
      />
      <div className="flex items-center gap-2">
        {moment !== null ? (
          <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
            <input type="checkbox" checked={pin} onChange={(e) => setPin(e.target.checked)} className="accent-[var(--primary)]" />
            at <span className="tabular text-foreground">{mediaTime(moment)}</span>
          </label>
        ) : (
          <span className="text-[11px] text-muted-foreground">Click the waveform to pin it to a moment.</span>
        )}
        {/* A click is a choice, so it holds — but playback has moved on since,
            and this is the way back to where the ear now is. */}
        {pin && clickedAt !== null && playingAt !== null && Math.abs(playingAt - clickedAt) > MOVED_ON_S && (
          <button
            type="button"
            onClick={() => {
              player.clearMark();
              setPinnedAt(playingAt);
            }}
            className="text-[11px] text-primary hover:underline"
            title="Pin it to where it is playing now instead"
          >
            use {mediaTime(playingAt)}
          </button>
        )}
        {error && <span className="text-xs text-removed">{error}</span>}
        <Button type="submit" size="sm" className="ml-auto" disabled={busy || !text.trim()}>
          {busy && <Loader2 className="animate-spin" />}
          Post
        </Button>
      </div>
    </form>
  );
}

function CommentRow({ comment, master }: { comment: ApiComment; master: PlayerTrack | null }) {
  const player = usePlayer();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const remove = () => {
    setBusy(true);
    webApi(`/comments/${encodeURIComponent(comment.id)}`, json("DELETE"))
      .then(() => {
        void queryClient.invalidateQueries({ queryKey: ["web", "comments", comment.seedId] });
        void queryClient.invalidateQueries({ queryKey: ["web", "seed", comment.seedId] });
      })
      .catch(() => setBusy(false));
  };
  return (
    <li className="flex gap-3 px-4 py-3">
      <Link to={profilePath(comment.author.handle)} className="shrink-0">
        <Avatar name={comment.author.username} url={comment.author.avatarUrl} className="size-7" />
      </Link>
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="flex flex-wrap items-baseline gap-x-2 text-xs">
          <Link to={profilePath(comment.author.handle)} className="font-medium hover:underline">
            {comment.author.username}
          </Link>
          {comment.atMs !== null && (
            <button
              type="button"
              disabled={!master}
              onClick={() => master && player.seekToSeconds(master, comment.atMs! / 1000)}
              className="tabular cursor-pointer text-primary hover:underline disabled:cursor-default"
              title="Play from here"
            >
              at {mediaTime(comment.atMs / 1000)}
            </button>
          )}
          <span className="text-muted-foreground">{relativeTime(comment.createdAt)}</span>
        </p>
        <p className="whitespace-pre-line break-words text-sm">{comment.body}</p>
      </div>
      {comment.canDelete && (
        <Button size="icon" variant="ghost" className="size-7 shrink-0" onClick={remove} disabled={busy} aria-label="Delete comment" title="Delete">
          <Trash2 className="size-3.5" />
        </Button>
      )}
    </li>
  );
}
